import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Mail, Phone, Send, Copy, Check, FileText } from 'lucide-react';
import { getFaviconUrl, getHostname } from '../../utils/leadPanelUtils';
import { isPhoneColumn } from '../../utils/validation';

interface CellContentPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRect: DOMRect | null;
  content: string;
  columnName?: string;
  isEmail?: boolean;
  isUrl?: boolean;
  companyName?: string;
  onSendEmail?: () => void;
  isPublicView?: boolean;
}

export const CellContentPopover: React.FC<CellContentPopoverProps> = ({
  isOpen,
  onClose,
  anchorRect,
  content,
  columnName,
  isEmail,
  isUrl,
  companyName,
  onSendEmail,
  isPublicView,
}) => {
  // Detect phone from column name
  const isPhone = isPhoneColumn(columnName || '');
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom' as 'bottom' | 'top', arrowLeft: 170 });
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Estimate content height for positioning
  const isLongContent = content.length > 300;
  const estimatedHeight = isEmail ? 160 : isPhone ? 140 : isUrl ? 120 : (isLongContent ? 320 : 200);

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !anchorRect) return;

    const w = 340;
    const pad = 16;
    const off = 8;

    let left = anchorRect.left + anchorRect.width / 2 - w / 2;
    let arrowLeft = w / 2;

    if (left < pad) {
      arrowLeft -= (pad - left);
      left = pad;
    } else if (left + w > window.innerWidth - pad) {
      const ov = left + w - (window.innerWidth - pad);
      arrowLeft += ov;
      left = window.innerWidth - w - pad;
    }
    arrowLeft = Math.max(24, Math.min(w - 24, arrowLeft));

    let top = anchorRect.bottom + off + 6;
    let placement: 'bottom' | 'top' = 'bottom';

    if (top + estimatedHeight > window.innerHeight - pad) {
      top = anchorRect.top - off - estimatedHeight - 6;
      placement = 'top';
      if (top < pad) {
        top = anchorRect.bottom + off + 6;
        placement = 'bottom';
      }
    }

    setPosition({ top, left, placement, arrowLeft });
    requestAnimationFrame(() => setIsVisible(true));
  }, [isOpen, anchorRect, content, isEmail, estimatedHeight]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 150);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen || !anchorRect) return null;

  const hostname = isUrl ? getHostname(content) : null;
  const isDescription = columnName?.toLowerCase().includes('description') || columnName?.toLowerCase().includes('bio') || columnName?.toLowerCase().includes('about');

  return (
    <>
      <style>{`
        @keyframes popIn { from { opacity: 0; transform: scale(0.96) translateY(4px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes popOut { from { opacity: 1; } to { opacity: 0; transform: scale(0.98); } }
        .content-scroll::-webkit-scrollbar { width: 4px; }
        .content-scroll::-webkit-scrollbar-track { background: transparent; }
        .content-scroll::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 2px; }
        .content-scroll::-webkit-scrollbar-thumb:hover { background: #D1D5DB; }
      `}</style>

      <div className="fixed inset-0 z-[299]" onMouseDown={(e) => { e.stopPropagation(); handleClose(); }} />

      {/* Arrow */}
      <div
        className="fixed z-[301] transition-opacity duration-150"
        style={{
          left: position.left + position.arrowLeft - 6,
          top: position.placement === 'bottom' ? position.top - 6 : position.top + estimatedHeight,
          opacity: isVisible ? 1 : 0,
        }}
      >
        <svg width="12" height="6" viewBox="0 0 12 6" fill="none">
          <path d={position.placement === 'bottom' ? "M6 0L12 6H0L6 0Z" : "M6 6L0 0H12L6 6Z"} fill="white" />
        </svg>
      </div>

      <div
        ref={popoverRef}
        className="fixed z-[300] bg-white rounded-2xl flex flex-col"
        style={{
          top: position.top,
          left: position.left,
          width: 340,
          maxHeight: isLongContent ? 380 : 280,
          animation: isVisible ? 'popIn 180ms cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'popOut 120ms ease-out forwards',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08), 0 20px 25px -5px rgba(0,0,0,0.08)',
        }}
      >
        {/* Header */}
        <div className="px-3 py-2.5 flex items-center justify-between border-b border-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            {isUrl && hostname && (
              <img
                src={getFaviconUrl(hostname, 32)}
                alt=""
                className="w-4 h-4 rounded"
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
            )}
            {isEmail && (
              <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
                <Mail size={10} className="text-blue-600" />
              </div>
            )}
            {isPhone && (
              <div className="w-4 h-4 rounded bg-green-100 flex items-center justify-center">
                <Phone size={10} className="text-green-600" />
              </div>
            )}
            {isDescription && !isUrl && !isEmail && !isPhone && (
              <div className="w-4 h-4 rounded bg-gray-100 flex items-center justify-center">
                <FileText size={10} className="text-gray-500" />
              </div>
            )}
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
              {columnName || (isEmail ? 'Email' : isPhone ? 'Phone' : isUrl ? 'URL' : 'Content')}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              title="Copy"
            >
              {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
            </button>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all"
            >
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto content-scroll">
          {isUrl ? (
            <div className="p-3">
              <a
                href={content.startsWith('http') ? content : `https://${content}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                  <img
                    src={getFaviconUrl(hostname!, 64)}
                    alt=""
                    className="w-5 h-5"
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span class="text-gray-400 text-sm font-medium">' + (companyName?.[0] || hostname?.[0] || '?').toUpperCase() + '</span>';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                      {companyName || hostname}
                    </span>
                    <ExternalLink size={11} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                  <p className="text-[11px] text-gray-500 truncate mt-0.5">{content}</p>
                </div>
              </a>
            </div>
          ) : isEmail ? (
            <div className="p-3 space-y-3">
              <a
                href={`mailto:${content}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-blue-600" />
                </div>
                <span className="text-[13px] font-medium text-gray-900 break-all flex-1">{content}</span>
                <ExternalLink size={14} className="text-blue-400 flex-shrink-0" />
              </a>
              {onSendEmail && !isPublicView && (
                <button
                  onClick={onSendEmail}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-[12px] font-medium transition-colors"
                >
                  <Send size={13} />
                  Send email
                </button>
              )}
            </div>
          ) : isPhone ? (
            <div className="p-3">
              <a
                href={`tel:${content}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-green-600" />
                </div>
                <span className="text-[13px] font-medium text-gray-900 break-all flex-1">{content}</span>
                <ExternalLink size={14} className="text-green-400 flex-shrink-0" />
              </a>
            </div>
          ) : (
            /* Description/Text content - cleaner design */
            <div className="px-4 py-3">
              <p className="text-[13px] text-gray-700 leading-[1.7] whitespace-pre-wrap">
                {content}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
